import React, { useMemo, useState } from "react"
import { Dropdown } from "antd"
import { MoreOutlined } from "@ant-design/icons"
import { getPlanMetadataCategoryIcon } from "./planMetadataCategoryIcons"
import { getPlanFeatureSections } from "../../utils/plansPricingPayload"
import {
  formatPlanCardLastUpdated,
  formatPlanPriceAmount,
  getPlanGridPriceDisplay,
} from "../../utils/plansPricingGrid"
import AccordionChevron from "./AccordionChevron"
import { PLAN_METADATA_SECTION_LABEL_KEYS } from "./planMetadataSections"

const PlansPricingPlanCard = ({
  plan,
  t,
  tFeatures,
  actionMenu,
  getStatusTag,
  getTypeLabel,
  getCategoryLabel,
}) => {
  const [expandedKeys, setExpandedKeys] = useState([])

  const priceDisplay = useMemo(
    () => getPlanGridPriceDisplay(plan, t),
    [plan, t]
  )

  const lang = localStorage.getItem("language") || "ar"
  const sections = useMemo(
    () => getPlanFeatureSections(plan, lang),
    [plan, lang]
  )

  const toggleSection = (key) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const imagesIncludedLabel = (
    t?.images_included_template || "{count} Images Included"
  ).replace("{count}", String(plan.images ?? 0))

  const subscribersDisplay =
    plan.subscribers != null && !Number.isNaN(Number(plan.subscribers))
      ? Number(plan.subscribers).toLocaleString()
      : "0"

  const lastUpdatedDisplay = formatPlanCardLastUpdated(plan.lastUpdated)

  return (
    <article
      className={`plans-pricing-plan-card ${
        plan.isPopular ? "is-popular" : ""
      }`}
    >
      {plan.isPopular && (
        <div className="plans-pricing-plan-card-ribbon">
          {t?.most_popular || "Most Popular"}
        </div>
      )}

      <div className="plans-pricing-plan-card-header">
        <div className="plans-pricing-plan-card-header-text">
          <h3 className="plans-pricing-plan-card-name">{plan.name}</h3>
          {plan.description ? (
            <p className="plans-pricing-plan-card-tagline">{plan.description}</p>
          ) : null}
          <div className="plans-pricing-plan-card-badges">
            <span className="plans-pricing-plan-card-type-pill">
              {getTypeLabel?.(plan.type) || plan.type}
            </span>
            {plan.category ? (
              <span
                className={`plans-pricing-plan-card-type-pill ${
                  plan.category === "Organization"
                    ? "plans-pricing-plan-card-type-pill--organization"
                    : ""
                }`}
              >
                {getCategoryLabel?.(plan.category) || plan.category}
              </span>
            ) : null}
            {getStatusTag(plan.status)}
          </div>
        </div>
        {actionMenu ? (
          <Dropdown
            menu={actionMenu}
            trigger={["click"]}
            overlayClassName="plans-pricing-action-dropdown"
          >
            <button
              type="button"
              className="plans-pricing-plan-card-menu"
              aria-label={t?.table?.actions || "Actions"}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        ) : null}
      </div>

      <div className="plans-pricing-plan-card-body">
        <div className="plans-pricing-plan-card-price">
          {priceDisplay.main}
          {priceDisplay.period && (
            <span className="plans-pricing-plan-card-price-period">
              {priceDisplay.period}
            </span>
          )}
          {priceDisplay.note && (
            <span className="plans-pricing-plan-card-price-note">
              {priceDisplay.note}
            </span>
          )}
        </div>

        <p className="plans-pricing-plan-card-images">{imagesIncludedLabel}</p>

        {priceDisplay.yearlyPromo && (
          <div className="plans-pricing-plan-card-promo">
            {priceDisplay.yearlyPromo}
          </div>
        )}

        {sections.length > 0 && (
          <div className="plans-pricing-plan-card-accordions">
            {sections.map((section, index) => {
              const Icon = getPlanMetadataCategoryIcon(section.key)
              const isOpen = expandedKeys.includes(section.key)
              const label =
                tFeatures?.[PLAN_METADATA_SECTION_LABEL_KEYS[section.key]] ||
                section.label

              return (
                <div
                  key={section.key}
                  className={`plans-pricing-plan-card-accordion ${
                    isOpen ? "is-open" : ""
                  } ${index === sections.length - 1 ? "is-last" : ""}`}
                >
                  <button
                    type="button"
                    className="plans-pricing-plan-card-accordion-header"
                    onClick={() => toggleSection(section.key)}
                    aria-expanded={isOpen}
                  >
                    <span className="plans-pricing-plan-card-accordion-title">
                      <Icon className="plans-pricing-plan-card-accordion-icon" />
                      <span className="plans-pricing-plan-card-accordion-label">
                        {label}
                      </span>
                    </span>
                    <AccordionChevron
                      open={isOpen}
                      className="plans-pricing-plan-card-chevron"
                    />
                  </button>
                  {isOpen && section.items.length > 0 && (
                    <ul className="plans-pricing-plan-card-feature-list">
                      {section.items.map((item, i) => (
                        <li key={`${section.key}-${i}`}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {plan.addonPerPhoto != null && (
          <div className="plans-pricing-plan-card-addon">
            <div className="plans-pricing-plan-card-addon-title">
              {t?.optional_addon || "Optional Add-On"}
            </div>
            <p className="plans-pricing-plan-card-addon-rate">
              SAR {formatPlanPriceAmount(plan.addonPerPhoto)}{" "}
              {t?.per_photo || "per photo"}
            </p>
          </div>
        )}
      </div>

      <footer className="plans-pricing-plan-card-footer">
        <div className="plans-pricing-plan-card-footer-item">
          <span className="plans-pricing-plan-card-footer-label">
            {t?.table?.subscribers || "Subscribers"}
          </span>
          <strong className="plans-pricing-plan-card-footer-value">
            {subscribersDisplay}
          </strong>
        </div>
        <div className="plans-pricing-plan-card-footer-item plans-pricing-plan-card-footer-item--end">
          <span className="plans-pricing-plan-card-footer-label">
            {t?.table?.last_updated || "Last Updated"}
          </span>
          <strong className="plans-pricing-plan-card-footer-value">
            {lastUpdatedDisplay}
          </strong>
        </div>
      </footer>
    </article>
  )
}

export default PlansPricingPlanCard
